import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { POLL_NORMAL } from '@/api/polling'
import { queryKeys } from '@/api/queries'
import type { components } from '@/api/schema'
import { QuoteBuilderApiError, useQuoteBuilderApi, type QuoteBuilderApi } from '../api/client'
import { reviewQueryKey } from '../review/review-api'

// Starting a project from an accepted quote (USA-1033): what still stops the start, and the project
// a quote produced. The project starts on its own once signed and paid (USA-1334), its POs held for
// Sales's PO review on the project (USA-1333).

type Schemas = components['schemas']

export type ProjectStart = Schemas['QbProjectStartRead']
export type ProjectStartBlocker = Schemas['QbProjectStartBlocker']
export type QuoteProject = Schemas['QbQuoteProjectRead']
export type ProjectSourceQuote = Schemas['QbProjectSourceQuoteRead']
export type ProjectPoReview = Schemas['QbProjectPoReviewRead']
export type HeldPurchaseOrder = Schemas['QbHeldPurchaseOrderRead']
export type PurchaseOrderItem = Schemas['QbPurchaseOrderItemRead']
type ProjectDetail = Schemas['InternalProjectDetailRead']

export const projectStartQueryKey = (quoteId: string) => ['quote-project', quoteId] as const

export const projectStartQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: projectStartQueryKey(quoteId),
    queryFn: ({ signal }) => api.request<ProjectStart>(`/quotes/${quoteId}/project`, { signal }),
    // Dropship placement cuts the POs in the background right after the start.
    refetchInterval: (query) => (query.state.data?.project?.placement === 'cutting' ? 2000 : false),
  })

/** Everything a start changes: the quote, its workflow, its project and its Review. */
export async function invalidateAfterStart(client: QueryClient, quoteId: string) {
  await Promise.all([
    client.invalidateQueries({ queryKey: ['quote', quoteId] }),
    client.invalidateQueries({ queryKey: ['quote-workflow', quoteId] }),
    client.invalidateQueries({ queryKey: projectStartQueryKey(quoteId) }),
    client.invalidateQueries({ queryKey: reviewQueryKey(quoteId) }),
  ])
}

/** The quote a project was started from, for the project page; `null` for other projects. */
export function useProjectSourceQuote(projectUuid: string) {
  const api = useQuoteBuilderApi()
  return useQuery({
    queryKey: ['projects', 'source-quote', projectUuid],
    queryFn: ({ signal }) =>
      api.request<ProjectSourceQuote | null>(`/projects/${projectUuid}/quote`, {
        signal,
      }),
    enabled: !!projectUuid,
    staleTime: POLL_NORMAL,
  })
}

// ── PO review (USA-1333) ──────────────────────────────────────────────────────

export const poReviewQueryKey = (projectUuid: string) => ['projects', 'po-review', projectUuid] as const

/** The project's PO review; `null` for a project whose POs did not wait for one. */
export function useProjectPoReview(projectUuid: string) {
  const api = useQuoteBuilderApi()
  return useQuery({
    queryKey: poReviewQueryKey(projectUuid),
    queryFn: ({ signal }) =>
      api.request<ProjectPoReview | null>(`/projects/${projectUuid}/po-review`, {
        signal,
      }),
    enabled: !!projectUuid,
    // Dropship cuts the held POs in the background right after the start.
    refetchInterval: (query) => (query.state.data?.state === 'creating' ? 2000 : false),
  })
}

export const poReviewWaitingQueryKey = ['po-review-waiting'] as const

/** How many projects wait on the signed-in salesperson's PO review: the sidebar's Projects badge (USA-1335). */
export function usePoReviewWaitingCount() {
  const api = useQuoteBuilderApi()
  return useQuery({
    queryKey: poReviewWaitingQueryKey,
    queryFn: ({ signal }) => api.request<Schemas['QbPoReviewWaitingRead']>('/po-reviews/waiting', { signal }),
    select: (read) => read.count,
    refetchInterval: 60_000,
    staleTime: 30_000,
  }).data ?? 0
}

/** Everything a PO review change touches: the review, the project, its timeline, actions and POs. */
function useRefreshProject(projectUuid: string) {
  const client = useQueryClient()
  return (review: ProjectPoReview) => {
    client.setQueryData(poReviewQueryKey(projectUuid), review)
    // The rows show the new marks at once; their sent status follows when the project reloads.
    const marks = new Map(
      review.purchase_orders.map((item) => [
        item.uuid,
        item.reviewed_at ? ('reviewed' as const) : ('needs_review' as const),
      ]),
    )
    client.setQueryData<ProjectDetail>(queryKeys.projects.detail(projectUuid), (project) =>
      project
        ? {
            ...project,
            orders: project.orders?.map((order) => ({
              ...order,
              purchase_orders: order.purchase_orders?.map((po) =>
                marks.has(po.uuid) ? { ...po, po_review: marks.get(po.uuid) } : po,
              ),
            })),
          }
        : project,
    )
    void client.invalidateQueries({ queryKey: queryKeys.projects.detail(projectUuid) })
    void client.invalidateQueries({ queryKey: ['projects', 'timeline', projectUuid] })
    void client.invalidateQueries({ queryKey: queryKeys.projects.actions(projectUuid) })
    void client.invalidateQueries({ queryKey: ['projects', 'with-actions'] })
    void client.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    void client.invalidateQueries({ queryKey: poReviewWaitingQueryKey })
  }
}

/** Set who one held PO is emailed to (To and CC). */
export function useUpdateHeldPurchaseOrderRecipients(projectUuid: string) {
  const api = useQuoteBuilderApi()
  const refresh = useRefreshProject(projectUuid)
  return useMutation({
    mutationFn: ({
      purchaseOrderUuid,
      recipient,
    }: {
      purchaseOrderUuid: string
      recipient: { email: string; name: string | null; cc_emails: string[] }
    }) =>
      api.request<ProjectPoReview>(
        `/projects/${projectUuid}/po-review/purchase-orders/${purchaseOrderUuid}/recipients`,
        { method: 'PUT', body: JSON.stringify(recipient) },
      ),
    onSuccess: refresh,
  })
}

/** Mark held POs reviewed, or not; marking the last one sends every PO to its manufacturer. */
export function useMarkPurchaseOrdersReviewed(projectUuid: string) {
  const api = useQuoteBuilderApi()
  const refresh = useRefreshProject(projectUuid)
  return useMutation({
    mutationFn: ({ uuids, reviewed }: { uuids: string[]; reviewed: boolean }) =>
      api.request<ProjectPoReview>(`/projects/${projectUuid}/po-review/reviewed`, {
        method: 'POST',
        body: JSON.stringify({ purchase_order_uuids: uuids, reviewed }),
      }),
    onSuccess: refresh,
  })
}

/** What a refused PO review change says, in Sales's words. */
export function apiErrors(error: unknown, fallback: string): string[] {
  if (error instanceof QuoteBuilderApiError) {
    const detail = error.body.detail as { errors?: unknown; message?: unknown } | string | undefined
    if (typeof detail === 'string') return [detail]
    if (Array.isArray(detail?.errors) && detail.errors.length) return detail.errors.map(String)
    if (typeof detail?.message === 'string') return [detail.message]
  }
  return [fallback]
}
